import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { getSettings } from '@/lib/localStorage';
import { applyTheme } from '@/lib/theme';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { isRepertoireSubdomain } from '@/lib/subdomain';
import Home from '@/pages/Home';
import { NavigationLayout } from '@/components/NavigationLayout';
import { SharedReportProvider } from '@/contexts/SharedReportContext';
import { ShareProvider } from '@/contexts/ShareContext';
import { AboutProvider } from '@/contexts/AboutContext';
import { useGlobalWakeLock } from '@/hooks/useGlobalWakeLock';
import { ToastProvider, useToast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';

const Settings = lazy(() => import('@/pages/Settings'));
const PracticeLog = lazy(() => import('@/pages/PracticeLog'));
const Report = lazy(() => import('@/pages/Report'));
const PracticePlan = lazy(() => import('@/pages/PracticePlan'));
const LessonPlan = lazy(() => import('@/pages/LessonPlan'));
const RepertoireList = lazy(() => import('@/pages/RepertoireList'));
const RepertoireDetail = lazy(() => import('@/pages/RepertoireDetail'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const SharedPieceDetail = lazy(() => import('@/pages/SharedPieceDetail'));

function PageFallback() {
  return (
    <div className="flex items-center justify-center min-h-[250px] w-full text-muted-foreground/60">
      <span className="material-icons animate-spin text-2xl text-primary">sync</span>
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

function AppRoutes() {
  const { isPasswordRecovery } = useAuth();
  const isRepertoire = isRepertoireSubdomain();

  // If the user arrived via a password recovery link, show the reset form
  if (isPasswordRecovery) {
    return (
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="*" element={<ResetPassword />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        {/* Timer routes */}
        <Route path="/" element={isRepertoire ? <Navigate to="/repertoire" replace /> : <Home />} />
        <Route path="/practice-plan" element={<PracticePlan />} />
        <Route path="/lesson-plan" element={<LessonPlan />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/practice-log" element={<PracticeLog />} />
        <Route path="/report" element={<Report />} />
        <Route path="/report/:token" element={<Report />} />
        <Route path="/report/:token/piece/:pieceId" element={<SharedPieceDetail />} />
        <Route path="/report/piece/:pieceId" element={<SharedPieceDetail />} />
        <Route path="/r/:id" element={<Report />} />
        <Route path="/r/:id/piece/:pieceId" element={<SharedPieceDetail />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Repertoire routes (auth-gated / promo) */}
        <Route path="/repertoire" element={<RepertoireList />} />
        <Route path="/repertoire/:id" element={<ProtectedRoute><RepertoireDetail /></ProtectedRoute>} />
      </Routes>
    </Suspense>
  );
}

function AppContent() {
  const { toast } = useToast();
  useGlobalWakeLock();

  useEffect(() => {
    // Initialize settings from local storage
    const localSettings = getSettings();
    const currentTheme = localSettings.theme || 'dark';
    applyTheme(currentTheme);
  }, []);

  useEffect(() => {
    // Service worker updates only apply in production mode
    if (import.meta.env.DEV) return;

    let reloaded = false;
    let hasShownUpdateToast = false;

    const triggerReload = () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    };

    const isPublicOrSharedRoute = () => {
      const path = window.location.pathname;
      return path.startsWith('/r/') || path.startsWith('/report/') || path === '/reset-password';
    };

    const showUpdateToast = (waitingWorker: ServiceWorker) => {
      if (hasShownUpdateToast) return;
      if (isPublicOrSharedRoute()) return;
      hasShownUpdateToast = true;

      toast({
        title: "Update Available",
        description: "A new version of the app is ready. Click Update to apply changes.",
        action: (
          <ToastAction
            altText="Update"
            onClick={() => {
              (window as any).__userInitiatedSWUpdate = true;
              try {
                waitingWorker.postMessage({ type: 'SKIP_WAITING' });
              } catch (err) {
                console.warn('Failed to send SKIP_WAITING to service worker:', err);
              }
              // Listen for worker activation
              waitingWorker.addEventListener('statechange', () => {
                if (waitingWorker.state === 'activated') {
                  triggerReload();
                }
              });
              // Direct fallback reload if controllerchange / statechange does not fire
              setTimeout(() => {
                triggerReload();
              }, 400);
            }}
          >
            Update
          </ToastAction>
        ),
        duration: Infinity,
      });
    };

    const checkForWaitingWorker = () => {
      if (hasShownUpdateToast || isPublicOrSharedRoute()) return;

      const existingReg = (window as any).__swWaitingRegistration as ServiceWorkerRegistration | undefined;
      if (existingReg?.waiting) {
        showUpdateToast(existingReg.waiting);
        return;
      }

      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistration().then((registration) => {
          if (registration && registration.waiting) {
            showUpdateToast(registration.waiting);
          }
        }).catch(err => {
          console.warn('Failed to get service worker registration:', err);
        });
      }
    };

    const handleUpdate = (e: Event) => {
      const registration = (e as CustomEvent).detail as ServiceWorkerRegistration;
      const waitingWorker = registration?.waiting;
      if (waitingWorker) {
        showUpdateToast(waitingWorker);
      }
    };

    window.addEventListener('sw-update-ready', handleUpdate);
    window.addEventListener('popstate', checkForWaitingWorker);

    checkForWaitingWorker();

    return () => {
      window.removeEventListener('sw-update-ready', handleUpdate);
      window.removeEventListener('popstate', checkForWaitingWorker);
    };
  }, [toast]);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <NavigationLayout>
        <AppRoutes />
      </NavigationLayout>
      <Toaster />
    </Router>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SharedReportProvider>
            <ShareProvider>
              <AboutProvider>
                <ToastProvider>
                  <AppContent />
                </ToastProvider>
              </AboutProvider>
            </ShareProvider>
          </SharedReportProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
